/**
 * DOM drawing, animations and frame control.
 * @module utils/drawing
 */

/**
 * Requests an animation frame.
 * @param {Function} callback - The function to call om frame/timeout
 * @returns {Number} - The frame/timeout id
 */
export function frame(callback) {
  return (typeof requestAnimationFrame === 'undefined') 
    ? setTimeout(callback, 0) : requestAnimationFrame(callback);
}

/**
 * Cancels a frame request.
 * @param {Number} id - The frame/timeout id
 */
export function unframe(id) {
  (typeof cancelAnimationFrame === 'undefined') 
    ? clearTimeout(id) : cancelAnimationFrame(id); 
}

/**
 * Creates and returns a debounced version of a function.
 * @param {Function} callback - The function to debounce.
 * @returns {Function} - The debounced callback function.
 */
export function debounce(callback) {
  let id;
  return event => {
    unframe(id);
    id = frame(_ => callback(event));
  }
}

/**
 * Triggers a layout reflow for a specific element.
 * @param {HTMLElement} element - The element to read for reflow.
 */
export function redraw(element) {
  element.offsetWidth;
}

/**
 * Creates and returns a new DOM element.
 * @param {String} type - The element type.
 * @param {Map} attributes - The element attributes.
 * @param {String} [html=null] - If set the returned attribute will be the first node of the passed html.
 * @returns {HTMLElement}
 */
export function newElement(type, attributes, html = null) {
  const el = document.createElement(type); 
  if (!el) return null;
  Object.keys(attributes).forEach(key => el[key] = attributes[key]);
  if (html) {
    el.innerHTML = html;
    return el.firstChild;
  } else {
    return el;
  }
}

/**
 * Emulates jQuery.slideDown
 * @param {HTMLElement} element - The element to animate.
 * @param {Number} duration - The length of the animation.
 * @param {Function} done - Callback once completed.
 */
export function slideDown(element, duration, done) {
  element.style.transitionProperty = 'height, margin, padding';
  element.style.transitionDuration = duration + 'ms';
  element.style.boxSizing = 'border-box';
  element.style.height = element.offsetHeight + 'px';
  element.offsetHeight;
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  window.setTimeout(_ => {
    element.style.display = 'none';
    element.style.removeProperty('height');
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
    done();
  }, duration);
}

/**
 * Emulates jQuery.slideUp
 * @param {HTMLElement} element - The element to animate.
 * @param {Number} duration - The length of the animation.
 * @param {Function} done - Callback once completed.
 */
export function slideUp(element, duration, done) {
  element.style.removeProperty('display');
  let display = window.getComputedStyle(element).display;
  if (display === 'none') display = 'block';
  element.style.display = display;
  let height = element.offsetHeight;
  element.style.overflow = 'hidden';
  element.style.height = 0;
  element.style.paddingTop = 0;
  element.style.paddingBottom = 0;
  element.style.marginTop = 0;
  element.style.marginBottom = 0;
  element.offsetHeight;
  element.style.boxSizing = 'border-box';
  element.style.transitionProperty = "height, margin, padding";
  element.style.transitionDuration = duration + 'ms';
  element.style.height = height + 'px';
  element.style.removeProperty('padding-top');
  element.style.removeProperty('padding-bottom');
  element.style.removeProperty('margin-top');
  element.style.removeProperty('margin-bottom');
  window.setTimeout(_ => {
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition-duration');
    element.style.removeProperty('transition-property');
    done();
  }, duration);
}



// WEBPACK FOOTER //
// ./source/scripts/utils/drawing.js